import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Skeleton} from '@/components/ui/skeleton'
import {formatCopFull} from '@/lib/money'
import {cn} from '@/lib/utils'

export function SegmentedToggle<T extends string>({
  value,
  options,
  optionLabel,
  onChange,
  testId,
}: {
  value: T
  options: readonly T[]
  optionLabel: (option: T) => string
  onChange: (value: T) => void
  testId: string
}) {
  return (
    <div className="flex w-fit overflow-hidden rounded-lg border border-input" role="group">
      {options.map((option, i) => (
        <button
          key={option}
          type="button"
          aria-pressed={value === option}
          data-testid={`${testId}-${option}`}
          onClick={() => onChange(option)}
          className={cn(
            'px-3 py-1.5 text-sm whitespace-nowrap',
            i > 0 && 'border-l border-input',
            value === option ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'
          )}>
          {optionLabel(option)}
        </button>
      ))}
    </div>
  )
}

export const PROCEDENCIA_LABEL_KEY: Record<string, string> = {
  tenant: 'cartera.overrides.fuentePropiaOverride',
  partner_segmento: 'cartera.overrides.fuentePartnerSegmento',
  partner_general: 'cartera.overrides.fuentePartnerGeneral',
  base: 'cartera.overrides.fuenteBaseGlobal',
}

export function ProcedenciaNote({
  loading,
  resolution,
  testId = 'cartera-override-procedencia',
}: {
  loading: boolean
  resolution: {amount_cop: number; procedencia: string} | null | undefined
  testId?: string
}) {
  const {t} = useTranslation()
  if (loading) return <Skeleton className="h-4 w-48" />
  return (
    <p className="text-sm text-muted-foreground" data-testid={testId}>
      {!resolution
        ? t('cartera.overrides.sinPrecioVigente')
        : t('cartera.overrides.procedencia', {
            amount: formatCopFull(resolution.amount_cop),
            source: t(PROCEDENCIA_LABEL_KEY[resolution.procedencia] ?? 'cartera.overrides.fuenteBaseGlobal'),
          })}
    </p>
  )
}

/** Solo escribe el monto resultante en el campo del caller; nunca envía nada. */
export function DescuentoAtajo({baseAmount, onApply}: {baseAmount: number | null; onApply: (amount: number) => void}) {
  const {t} = useTranslation()
  const [pct, setPct] = useState('')

  if (baseAmount == null) return null
  const pctNum = Number(pct)
  const valid = pct.trim() !== '' && Number.isFinite(pctNum) && pctNum > 0 && pctNum < 100
  const preview = valid ? Math.round(baseAmount * (1 - pctNum / 100)) : null

  return (
    <div className="flex flex-wrap items-end gap-2 rounded-lg border border-dashed border-border p-3">
      <div className="space-y-1.5">
        <Label htmlFor="cartera-override-descuento-pct">{t('cartera.overrides.descuentoLabel')}</Label>
        <Input
          id="cartera-override-descuento-pct"
          data-testid="cartera-override-descuento-pct"
          type="number"
          min={1}
          max={99}
          value={pct}
          onChange={(e) => setPct(e.target.value)}
          className="w-24"
        />
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={preview == null}
        data-testid="cartera-override-descuento-aplicar"
        onClick={() => preview != null && onApply(preview)}>
        {preview != null
          ? t('cartera.overrides.descuentoAplicarConMonto', {amount: formatCopFull(preview)})
          : t('cartera.overrides.descuentoAplicar')}
      </Button>
    </div>
  )
}
